import { z } from 'zod';

/**
 * The alphabet an invite code is drawn from — **Crockford base32**.
 *
 * `I`, `L`, `O` and `U` are absent, and their absence is the whole point: this
 * code is not clicked, it is **read off one phone and typed into another** by a
 * guardian who may be doing it once in his life. An alphabet containing both `0`
 * and `O` turns a transcription slip into "that code is wrong", with no way for
 * either person to see which character disagreed.
 *
 * `U` is dropped for a different reason than the other three — Crockford
 * excludes it so the encoding cannot spell an obscenity by accident.
 */
export const GUARDIAN_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Twelve symbols — 32^12 ≈ 1.15 × 10^18.
 *
 * The entropy is what makes the code safe, **not** a rate limit on the accept
 * route: the code is the only thing standing between a stranger and a woman's
 * conversations, so it is sized so that guessing is not a strategy even against
 * an attacker the platform never notices.
 */
export const GUARDIAN_CODE_LENGTH = 12;

/** How many symbols a displayed group holds: `4K7M-9P2R-XTQ3`. */
const GROUP_SIZE = 4;

/**
 * What a person may type in place of a symbol the alphabet never emits.
 *
 * Crockford's own decoding rule. The code generator can never produce an `I`,
 * so an `I` in the input is unambiguously somebody's `1` — folding it is
 * strictly more correct than refusing it.
 */
const FOLDED: Readonly<Record<string, string>> = { I: '1', L: '1', O: '0', U: 'V' };

/**
 * The code as it was typed → the code as it is stored, or `null`.
 *
 * `null` means "this is not a code", never "this code is unknown" — whether a
 * code exists is a question only the database answers, and this function
 * deliberately cannot leak it.
 */
export function normalizeGuardianCode(input: string): string | null {
  const folded = [...input.toUpperCase()]
    .map((character) => FOLDED[character] ?? character)
    .filter((character) => GUARDIAN_CODE_ALPHABET.includes(character))
    .join('');

  return folded.length === GUARDIAN_CODE_LENGTH ? folded : null;
}

/** The stored code → what the ward reads aloud. Grouping is display only. */
export function formatGuardianCode(code: string): string {
  const groups: string[] = [];

  for (let index = 0; index < code.length; index += GROUP_SIZE) {
    groups.push(code.slice(index, index + GROUP_SIZE));
  }

  return groups.join('-');
}

/**
 * A code on the wire.
 *
 * It **normalizes as it parses**, so nothing downstream ever holds a display
 * form: the service hashes exactly what the schema produced, and a lookup can
 * never miss because the guardian typed the hyphens.
 */
export const GuardianCodeSchema = z
  .string()
  .transform((value) => normalizeGuardianCode(value))
  .refine((value): value is string => value !== null, {
    message: 'That is not a valid guardian code.',
  });
